import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import type { ClientLimits } from '../api/types';
import { useMediaUrl } from '../auth/ApiProvider';
import { useI18n } from '../i18n/I18nProvider';
import { toLogo } from '../media/logo';
import { toProfilePhoto } from '../media/profilePhoto';
import { useLimits } from '../hooks/LimitsProvider';
import { noSave } from './noSave';
import { Button, Notice, Row, Stack } from './primitives';
import { ErrorState } from './states';

export type AvatarSize = 'sm' | 'md' | 'lg';

/**
 * The first letter of a name, for an avatar with no photo.
 *
 * One letter, not two: Arabic letters placed side by side join into what reads as a word, not initials.
 */
export function initialOf(name: string | null | undefined): string {
  return [...(name ?? '').trim()][0] ?? '؟';
}

/**
 * A person's or a child's photo, or their initial when there is none.
 *
 * Decorative (`alt=""`): it always sits next to the name it belongs to, and a screen reader reading the
 * name twice helps nobody. A signed link lives fifteen minutes, so one that has expired falls back to the
 * initial rather than showing a broken image.
 */
export function Avatar({ name, photoUrl, size = 'md' }: {
  name: string | null | undefined;
  photoUrl?: string | null;
  size?: AvatarSize;
}) {
  return <ImageTile base="dg-avatar" name={name} url={photoUrl} size={size} />;
}

/** An image, or the name's initial where there is none or it will not load. What an avatar and a logo share. */
function ImageTile({ base, name, url, size }: {
  base: 'dg-avatar' | 'dg-logo';
  name: string | null | undefined;
  url?: string | null;
  size: AvatarSize;
}) {
  const media = useMediaUrl();
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const className = `${base} ${base}--${size}`;

  if (url && url !== failedUrl) {
    return <img {...noSave} className={className} src={media(url)} alt="" onError={() => setFailedUrl(url)} />;
  }
  return <span className={className} aria-hidden="true">{initialOf(name)}</span>;
}

/** An object URL for a chosen photo, revoked when the photo changes or the component goes away. */
function usePreviewUrl(photo: Blob | null): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!photo) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(photo);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [photo]);

  return url;
}

/**
 * A nursery's logo, or the first letter of its name when it has none.
 *
 * Decorative for the same reason an avatar is: the name is always beside it. A logo is not cropped, so it is
 * fitted inside its tile rather than filling it.
 */
export function TenantLogo({ name, logoUrl, size = 'md' }: {
  name: string | null | undefined;
  logoUrl?: string | null;
  size?: AvatarSize;
}) {
  return <ImageTile base="dg-logo" name={name} url={logoUrl} size={size} />;
}

/**
 * What differs between choosing a photo and choosing a logo: how the file is prepared, how the choice is
 * shown, and which words the controls use (`photo.*` or `logo.*`).
 */
interface ImageKind {
  prepare: (file: Blob, media: ClientLimits['media']) => Promise<Blob>;
  show: (url: string | null | undefined) => ReactNode;
  words: 'photo' | 'logo';
}

/**
 * A button that opens the file chooser and hands back the image already prepared — a photo cropped and
 * shrunk, a logo shrunk.
 *
 * `accept` includes HEIC so an iPhone's camera roll is offered; a browser that can decode it re-encodes it,
 * and one that cannot says so in plain words.
 */
export function ChooseImageButton({ label, prepare, onChosen, onUnreadable, busy }: {
  label: string;
  prepare: ImageKind['prepare'];
  onChosen: (image: Blob) => void;
  onUnreadable: () => void;
  /** Whatever happens to the image next is still under way, so another cannot be chosen yet. */
  busy?: boolean;
}) {
  const { media } = useLimits();
  const input = useRef<HTMLInputElement>(null);
  const [reading, setReading] = useState(false);

  async function choose(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Cleared so choosing the same file again still fires a change.
    event.target.value = '';
    if (!file) return;

    setReading(true);
    try {
      onChosen(await prepare(file, media));
    } catch {
      // Whatever went wrong — a format the browser cannot decode, a canvas it refused — the person can do
      // one thing about it: choose another file. Both preparers raise UnreadablePhotoError for all of it.
      onUnreadable();
    } finally {
      setReading(false);
    }
  }

  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        hidden
        onChange={(event) => void choose(event)}
      />
      <Button variant="secondary" busy={reading || busy} onClick={() => input.current?.click()}>{label}</Button>
    </>
  );
}

/** Choosing an image without saving it anywhere yet. `null` means none. */
function ImageField({ kind, value, onChange, hint }: {
  kind: ImageKind;
  value: Blob | null;
  onChange: (image: Blob | null) => void;
  hint?: string;
}) {
  const { t } = useI18n();
  const preview = usePreviewUrl(value);
  const [unreadable, setUnreadable] = useState(false);

  return (
    <Stack tight>
      <Row>
        {kind.show(preview)}
        <ChooseImageButton
          label={t(value ? `${kind.words}.change` : `${kind.words}.add`)}
          prepare={kind.prepare}
          onChosen={(image) => { setUnreadable(false); onChange(image); }}
          onUnreadable={() => setUnreadable(true)}
        />
        {value && <Button variant="ghost" onClick={() => onChange(null)}>{t(`${kind.words}.remove`)}</Button>}
      </Row>
      {hint && <p className="dg-muted dg-text-sm">{hint}</p>}
      {unreadable && <Notice tone="warning">{t(`${kind.words}.unreadable`)}</Notice>}
    </Stack>
  );
}

/**
 * The current image with Change and Remove, saving straight away.
 *
 * A chosen image is previewed first and saved only on "Save", because the result may not be what the person
 * expected — a square crop that cut something off, a logo that reads badly small.
 */
function ImagePicker({ kind, url, hint, onSave, onRemove }: {
  kind: ImageKind;
  url?: string | null;
  hint?: string;
  onSave: (image: Blob) => Promise<unknown>;
  onRemove: () => Promise<unknown>;
}) {
  const { t } = useI18n();
  const [chosen, setChosen] = useState<Blob | null>(null);
  const [unreadable, setUnreadable] = useState(false);
  const [done, setDone] = useState<'saved' | 'removed' | null>(null);
  const preview = usePreviewUrl(chosen);
  const words = kind.words;

  const save = useMutation({
    mutationFn: (image: Blob) => onSave(image),
    onSuccess: () => { setChosen(null); setDone('saved'); },
  });

  const remove = useMutation({
    mutationFn: () => onRemove(),
    onSuccess: () => setDone('removed'),
  });

  const error = save.error ?? remove.error;

  return (
    <Stack>
      <Row>
        {kind.show(preview ?? url)}
        {hint && <p className="dg-muted dg-text-sm">{hint}</p>}
      </Row>

      {error && <ErrorState error={error} />}
      {unreadable && <Notice tone="warning">{t(`${words}.unreadable`)}</Notice>}
      {chosen && <p className="dg-text-sm">{t(`${words}.preview`)}</p>}
      {done && !chosen && <Notice tone="success">{t(done === 'saved' ? `${words}.saved` : `${words}.removed`)}</Notice>}

      <Row>
        {chosen ? (
          <>
            <Button busy={save.isPending} busyLabel={t('action.saving')} onClick={() => save.mutate(chosen)}>
              {t(`${words}.save`)}
            </Button>
            <Button variant="ghost" disabled={save.isPending} onClick={() => setChosen(null)}>
              {t('action.cancel')}
            </Button>
          </>
        ) : (
          <>
            <ChooseImageButton
              label={t(url ? `${words}.change` : `${words}.add`)}
              prepare={kind.prepare}
              onChosen={(image) => {
                setUnreadable(false);
                setDone(null);
                save.reset();
                remove.reset();
                setChosen(image);
              }}
              onUnreadable={() => setUnreadable(true)}
            />
            {url && (
              <Button variant="danger" busy={remove.isPending} onClick={() => { setDone(null); remove.mutate(); }}>
                {t(`${words}.remove`)}
              </Button>
            )}
          </>
        )}
      </Row>
    </Stack>
  );
}

function photoKind(name: string | null | undefined): ImageKind {
  return {
    prepare: (file, media) => toProfilePhoto(file, media.profilePhotoMaximumEdge),
    show: (url) => <Avatar name={name} photoUrl={url} size="lg" />,
    words: 'photo',
  };
}

function logoKind(name: string | null | undefined): ImageKind {
  return {
    prepare: (file, media) => toLogo(file, media.logoMaximumEdge),
    show: (url) => <TenantLogo name={name} logoUrl={url} size="lg" />,
    words: 'logo',
  };
}

/**
 * Choosing a photo without saving it anywhere yet: for a form that creates the record first, as registering
 * a child does. The chosen photo is reported through `onChange`; `null` means none.
 */
export function PhotoField({ name, value, onChange, hint }: {
  name: string | null | undefined;
  value: Blob | null;
  onChange: (photo: Blob | null) => void;
  hint?: string;
}) {
  return <ImageField kind={photoKind(name)} value={value} onChange={onChange} hint={hint} />;
}

/** The current photo with Change and Remove, saving straight away. Used by "My account" and by the child page. */
export function PhotoPicker({ name, photoUrl, hint, onSave, onRemove }: {
  name: string | null | undefined;
  photoUrl?: string | null;
  hint?: string;
  onSave: (photo: Blob) => Promise<unknown>;
  onRemove: () => Promise<unknown>;
}) {
  return <ImagePicker kind={photoKind(name)} url={photoUrl} hint={hint} onSave={onSave} onRemove={onRemove} />;
}

/** Choosing a nursery's logo before the nursery exists, as registering one does. `null` means none. */
export function LogoField({ name, value, onChange, hint }: {
  name: string | null | undefined;
  value: Blob | null;
  onChange: (logo: Blob | null) => void;
  hint?: string;
}) {
  return <ImageField kind={logoKind(name)} value={value} onChange={onChange} hint={hint} />;
}

/** A nursery's current logo with Change and Remove, saving straight away. */
export function LogoPicker({ name, logoUrl, hint, onSave, onRemove }: {
  name: string | null | undefined;
  logoUrl?: string | null;
  hint?: string;
  onSave: (logo: Blob) => Promise<unknown>;
  onRemove: () => Promise<unknown>;
}) {
  return <ImagePicker kind={logoKind(name)} url={logoUrl} hint={hint} onSave={onSave} onRemove={onRemove} />;
}
